import React from "react"
import { Audio, Html5Video, OffthreadVideo, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from "remotion"

type DuckSegment = {
  startMs: number
  endMs: number
  duckOriginalAudio: boolean
  duckLevel: number
}

interface VideoTrackProps {
  src: string
  duckSegments?: DuckSegment[]
  /** Silence this copy (the scene layer draws the source several times and plays audio once). */
  muted?: boolean
  /** Source frames to skip, for a copy mounted inside a Sequence that starts mid-clip. */
  trimBefore?: number
  /** The Player's <video> element, for panels that mirror this copy instead of loading their own. */
  videoRef?: React.Ref<HTMLVideoElement>
}

function sourceVolumeAt(duckSegments: DuckSegment[], atFrame: number, fps: number): number {
  if (duckSegments.length === 0) return 1

  const currentMs = (atFrame / fps) * 1000
  let volume = 1

  for (const segment of duckSegments) {
    if (
      segment.duckOriginalAudio &&
      currentMs >= segment.startMs &&
      currentMs <= segment.endMs
    ) {
      const target = Math.max(0, Math.min(1, segment.duckLevel / 100))
      volume = Math.min(volume, target)
    }
  }

  return volume
}

export const VideoTrack: React.FC<VideoTrackProps> = ({ src, duckSegments = [], muted = false, trimBefore, videoRef }) => {
  if (!src) return null

  const { isPlayer } = getRemotionEnvironment()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const trim = trimBefore ? trimBefore : undefined

  if (isPlayer) {
    return (
      <Html5Video
        ref={videoRef}
        src={src}
        pauseWhenBuffering
        muted={muted}
        trimBefore={trim}
        volume={muted ? 0 : sourceVolumeAt(duckSegments, frame, fps)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(err) => {
          console.warn(`[Remotion] Source video failed in Player preview: ${err.message}`)
        }}
      />
    )
  }

  return (
    <OffthreadVideo
      src={src}
      muted={muted}
      trimBefore={trim}
      volume={(f) => sourceVolumeAt(duckSegments, f, fps)}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  )
}

/** The source video's audio on its own, for when every visual copy of the source is muted. */
export const SourceAudio: React.FC<{ src: string; duckSegments?: DuckSegment[] }> = ({ src, duckSegments = [] }) => {
  const { isPlayer } = getRemotionEnvironment()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  if (!src) return null
  return (
    <Audio
      src={src}
      pauseWhenBuffering
      volume={isPlayer ? sourceVolumeAt(duckSegments, frame, fps) : (f) => sourceVolumeAt(duckSegments, f, fps)}
    />
  )
}
